'use client';
import { useEffect, useState } from 'react';

interface UseAudioWaveformFromUrlProps {
  /** 音频 URL */
  audioUrl: string | null | undefined;
  /** 波形条数量，默认 40 */
  barCount?: number;
  /** 成片时长，默认 0 */
  totalDuration?: number;
}

interface UseAudioWaveformReturn {
  /** 波形数据数组 (0-100) */
  waveform: number[];
  /** 是否正在加载 */
  isLoading: boolean;
  /** 是否加载完成 */
  isLoaded: boolean;
}

interface ExtractWaveformFromUrlProps {
  audioUrl: string;
  barCount?: number;
  totalDuration?: number;
}

/**
 * 从音频 URL 提取波形数据
 * 无需依赖 audio 元素
 */
async function extractWaveformFromUrl({
  audioUrl,
  barCount = 40,
  totalDuration = 0,
}: ExtractWaveformFromUrlProps): Promise<number[]> {
  const AudioContextClass =
    window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  const audioContext = new AudioContextClass();

  try {
    const response = await fetch(audioUrl);
    const arrayBuffer = await response.arrayBuffer();

    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    const audioDuration = audioBuffer.duration;
    const sampleRate = audioBuffer.sampleRate;
    const targetDuration = totalDuration > 0 ? totalDuration : audioDuration;

    let channelData = audioBuffer.getChannelData(0);
    let barsToRender = barCount;
    let padCount = 0;

    if (targetDuration < audioDuration) {
      const targetSamples = Math.max(1, Math.floor(targetDuration * sampleRate));
      channelData = channelData.subarray(0, Math.min(targetSamples, channelData.length));
    } else if (targetDuration > audioDuration) {
      barsToRender = Math.max(1, Math.round((audioDuration / targetDuration) * barCount));
      padCount = barCount - barsToRender;
    }

    const samples = channelData.length;
    const samplesPerBar = Math.max(1, Math.floor(samples / barsToRender));

    const waveform: number[] = [];

    for (let i = 0; i < barsToRender; i++) {
      const start = i * samplesPerBar;
      if (start >= samples) {
        waveform.push(0);
        continue;
      }
      const end = Math.min(start + samplesPerBar, samples);

      let sumSquares = 0;
      for (let j = start; j < end; j++) {
        sumSquares += channelData[j] * channelData[j];
      }

      const rms = Math.sqrt(sumSquares / (end - start || 1));
      const normalized = rms * 100;
      const enhanced = Math.sqrt(normalized / 100) * 100;
      const value = Math.min(100, enhanced * 2);

      waveform.push(value);
    }

    if (padCount > 0) {
      waveform.push(...new Array(padCount).fill(0));
    }

    await audioContext.close();
    return waveform;
  } catch (error) {
    await audioContext.close();
    throw error;
  }
}

/**
 * 音频波形提取 Hook（从 URL）
 * 直接从音频 URL 提取波形数据，无需 audio 元素
 */
export function useAudioWaveformFromUrl({
  audioUrl,
  barCount = 40,
  totalDuration = 0,
}: UseAudioWaveformFromUrlProps): UseAudioWaveformReturn {
  const [waveform, setWaveform] = useState<number[]>(new Array(barCount).fill(0));
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!audioUrl || barCount <= 0) {
      setWaveform(new Array(barCount).fill(0));
      setIsLoaded(false);
      return;
    }

    let isMounted = true;

    const loadWaveform = async () => {
      setIsLoading(true);

      try {
        const extractedWaveform = await extractWaveformFromUrl({ audioUrl, barCount, totalDuration });

        if (isMounted) {
          setWaveform(extractedWaveform);
          setIsLoaded(true);
        }
      } catch {
        if (isMounted) {
          setWaveform(new Array(barCount).fill(30));
          setIsLoaded(false);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadWaveform();

    return () => {
      isMounted = false;
    };
  }, [audioUrl, barCount, totalDuration]);

  return {
    waveform,
    isLoading,
    isLoaded,
  };
}
